<template>
  <div>
    <h1 class="font-weight-bold mt-2 mb-4" style="font-size: 20px; line-height: 23px">
      Previsão e Projeção
    </h1>
    <v-row align="center">
      <v-col cols="12" md="6">
        <v-icon
          class="mr-2"
          size="16"
          color="white"
          :style="`height: 24px; width: 24px; border-radius: 50%;background-color: ${basinIconColor};`"
          >{{ basinIconName }}</v-icon
        >
        <span v-if="location" class="ml-2 pt-1" style="font-size: 16px">{{
          location.text
        }}</span>
      </v-col>
      <v-col cols="12" md="6">
        <span style="font-size: 12px; line-height: 16px">{{
          this.lastUpdateSiprecDaily()
        }}</span>
      </v-col>
    </v-row>
    <v-row align="center">
      <v-col cols="12">
        <v-tabs
          v-model="tabs"
          color="secondary lighten-1"
          background-color="transparent"
          slider-color="secondary lighten-1"
        >
          <v-tabs-slider color="secondary lighten-1"></v-tabs-slider>
          <v-tab href="#hydrological" style="text-transform: none">Hidrológica</v-tab>
          <v-tab href="#meteorological" style="text-transform: none">Meteorológica</v-tab>
        </v-tabs>
        <v-tabs-items v-model="tabs" class="transparent" touchless>
          <v-tab-item value="hydrological">
            <v-card color="mt-3 pa-2 primary darken-1">
              <v-card-title class="mb-0">
                <chart-header
                  :authorize="authorize"
                  @fullscreenChart="fullscreenChart"
                  @printChart="printChart"
                  @exportChart="exportChart($event)"
                  @exportCSV="exportCSV"
                  @exportXLS="exportXLS"
                >
                  <template v-slot:title> Dados hidrológicos </template>
                </chart-header>
              </v-card-title>

              <v-card-text>
                <h3 class="subtitle-1 white--text">Selecione o tipo do dado:</h3>
                <v-tabs
                  class="hidden-sm-and-down secondary--text text--lighten-1"
                  v-model="tab"
                  active-class="secondary lighten-1 primary--text text--darken-1"
                  background-color="primary darken-1"
                  slider-color="secondary lighten-1"
                >
                  <v-tab
                    class="tab-item-title"
                    v-for="(item, index) in items"
                    :key="index"
                    :id="item.id"
                    >{{ item.title }}</v-tab
                  >
                </v-tabs>

                <v-menu bottom left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      class="hidden-md-and-up tab-item-title transparent"
                      outlined
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ items[tab].title }}
                      <v-icon> mdi-menu-down </v-icon>
                    </v-btn>
                  </template>

                  <v-list>
                    <v-list-item
                      v-for="(item, index) in items"
                      :key="index"
                      @click="changeTabItem(index)"
                    >
                      {{ item.title }}
                    </v-list-item>
                  </v-list>
                </v-menu>

                <v-col
                  cols="12"
                  style="margin-left: 340px;"
                  class="mt-n14"
                  align-self="end"
                  align="start"
                >
                  <v-btn
                    link
                    @click="goToPage()"
                    class="mx-auto button-text secondary--text text--lighten-1 subtitle-2 font-weight-medium"
                    height="40"
                    text
                  >
                    <v-icon left size="24">language</v-icon>
                    <span>Consultar dados históricos</span>
                    <v-icon right size="24">mdi-open-in-new</v-icon>
                  </v-btn>
                </v-col>

                <v-tabs-items v-model="tab" class="primary darken-1" touchless>
                  <v-tab-item key="level-rate">
                    <v-card flat color="primary darken-1">
                      <v-card-text class="px-0 text-center">
                        <div v-if="showAnimation" style="height: 300px">
                          <loading-animation
                            @loopComplete="loadAnimationComplete"
                          ></loading-animation>
                        </div>
                        <permanence-chart
                          ref="permanenceChart"
                          v-show="!showAnimation"
                          :location="location"
                          :forecasts="forecasts"
                          :flowForecasts="flowForecasts"
                          :waterAvailability="waterAvailability"
                          :minDaysForecast="minDaysForecast"
                          :maxDaysForecast="maxDaysForecast"
                          :qoper = "qoper"
                          :q95 = "q95"
                          :q95_50 = "q95_50"
                          @chartRenderComplete="chartRenderComplete($event)"
                        >
                        </permanence-chart>
                      </v-card-text>
                    </v-card>
                  </v-tab-item>
                  <v-tab-item class="px-0" key="cubic-flow-rate">
                    <v-card flat color="primary darken-1">
                      <v-card-text class="px-0 text-center">
                        <div v-if="showAnimation" style="height: 300px">
                          <loading-animation
                            @loopComplete="loadAnimationComplete"
                          ></loading-animation>
                        </div>
                        <rain-flow-chart
                          ref="rainFlowChart"
                          v-show="!showAnimation"
                          :location="location"
                          :forecasts="forecasts"
                          :flowForecasts="flowForecasts"
                          :siprecDailyEstimated="siprecDailyEstimated"
                          :prevHidroDaily="prevHidroDaily"
                          :minDaysForecast="minDaysForecast"
                          :maxDaysForecast="maxDaysForecast"
                          @chartRenderComplete="chartRenderComplete($event)"
                        >
                        </rain-flow-chart>
                      </v-card-text>
                    </v-card>
                  </v-tab-item>
                </v-tabs-items>
              </v-card-text>
            </v-card>
          </v-tab-item>
          <v-tab-item value="meteorological">
            <meteorological-chart :location="location"></meteorological-chart>
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import momentTimeZone from "moment-timezone";
import Highcharts from "highcharts";
import Exporting from "highcharts/modules/export-data";
import LoadingAnimation from "../LoadingAnimation";
import RainFlowChart from "./RainFlowChart";
import PermanenceChart from "./PermanenceChart.vue";
import ChartHeader from "../ChartHeader.vue";
import {
  rainAndFlowChartAuthorization,
  permanenceChartAuthorization,
} from "../../../helpers/chart_export_authorization";
import MeteorologicalChart from "../MeteorologicalChart/MeteorologicalChart";
Exporting(Highcharts);
export default {
  name: "MonitoringWeeklyForecast",
  components: {
    MeteorologicalChart,
    LoadingAnimation,
    RainFlowChart,
    PermanenceChart,
    ChartHeader,
  },
  props: {
    location: {
      required: true,
    },
    forecasts: {
      required: true,
    },
    flowForecasts: {
      required: true,
    },
    siprecDailyEstimated: {
      require: true,
    },
    prevHidroDaily: {
      require: true,
    },
    waterAvailability: {
      required: true,
    },
    basinIcon: {
      type: Object,
      default: () => ({ name: "icon-infohidro-bacia", color: "#69D7A3" }),
    },
    from: {
      type: String,
    },
    qoper: {
      type: Number,
      required: false,
      default: null,
    },
    q95: {
      type: Number,
      required: false,
      default: null
    },
    q95_50: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data: () => ({
    tab: 0,
    tabs: 0,
    chartRendered: undefined,
    minDaysForecast: 29,
    maxDaysForecast: 6,
    loading: false,
    showAnimation: false,
    items: [
      {
        title: "Vazão e Permanência",
        id: "tab-permanence",
      },
      {
        title: "Chuva e Vazão",
        id: "tab-rain-flow",
      },
    ],
    authorize: [],
  }),
  created() {
    this.authorize = rainAndFlowChartAuthorization;
  },
  watch: {
    watchLocationAndForecastsValues() {
      if (
        this.location &&
        this.forecasts &&
        this.flowForecasts &&
        this.waterAvailability &&
        this.siprecDailyEstimated &&
        this.prevHidroDaily
      ) {
        this.loadCharts(this.tab);
      }
    },
    tab(value) {
      this.loadCharts(value);

      this.$gtag.event("tabs_" + this.from + "-WeeklyForecast", {
        event_category: "WeeklyForecast",
        event_label: "tab_index",
        value: value,
      });
    },
  },
  computed: {
    watchLocationAndForecastsValues() {
      return [this.location, this.forecasts, this.flowForecasts, this.waterAvailability, this.siprecDailyEstimated, this.prevHidroDaily];
    },
    basinIconName() {
      return this.basinIcon && this.basinIcon.name
        ? this.basinIcon.name
        : "icon-infohidro-bacia";
    },
    basinIconColor() {
      return this.basinIcon && this.basinIcon.color ? this.basinIcon.color : "#69D7A3";
    },
  },
  methods: {
    goToPage() {
      let routePath = '/RainEstimationHistoricalData/'+this.$route.name+'/'+this.location.locationid
      this.$router.push(routePath);
    },
    loadCharts(tab) {
      setTimeout(() => {
        if (tab === 1) {
          if (this.$refs.rainFlowChart && this.siprecDailyEstimated && this.prevHidroDaily) {
            this.$refs.rainFlowChart.chartPreprocessing();
            this.authorize = rainAndFlowChartAuthorization;
          }
        } else if (tab === 0) {
          if (this.$refs.permanenceChart) {
            this.$refs.permanenceChart.chartPreprocessing();
            this.authorize = permanenceChartAuthorization;
          }
        }
      }, 50);
    },
    chartRenderComplete(chart) {
      this.chartRendered = chart;
    },
    exportChart(typeFile) {
      this.chartRendered.exportChart({ type: typeFile });
    },
    exportCSV() {
      this.chartRendered.downloadCSV();
    },
    exportXLS() {
      this.chartRendered.downloadXLS();
    },
    fullscreenChart() {
      const fullscreenControl = new Highcharts.Fullscreen(this.chartRendered);
      fullscreenControl.open();
    },
    printChart() {
      this.chartRendered.print();
    },

    lastUpdateSiprecDaily() {
      let lastUpdate = this.forecasts.find((forecast) => forecast.sourceid === 26);

      if (lastUpdate) {
        const update = momentTimeZone(lastUpdate.updated).tz(lastUpdate.timezone);
        const runtime = momentTimeZone(lastUpdate.runtime).tz("UTC"); // Daily

        return `Siprec atualizado às ${update.format(
          "HH:mm [(BRT) do dia] DD/MM/YYYY"
        )}. Último modelo disponível do dia ${runtime.format("DD/MM/YYYY")}. Valores apresentados em milímetros (mm).`;
      }

      return "";
    },
    changeTabItem(tabIndex) {
      this.tab = tabIndex;
    },
  },
};
</script>

<style scoped>
.tab-item-title {
  color: var(--v-secondary-lighten1) !important;
  border: 1px solid var(--v-secondary-lighten1);
  text-transform: none;
}
#tab-rain-flow {
  border-radius: 4px 0px 0px 4px;
}
#tab-permanence {
  border-radius: 0px 4px 4px 0px;
}
</style>
